import { useEffect, useState } from 'react';
import technicianService from '../../api/technicianService';
import useAuth from '../../hooks/useAuth';
import { useNotifications } from '../../context/NotificationContext';
import Field from '../common/Field';
import LoadingSpinner from '../common/LoadingSpinner';
import AvailabilityManager from './AvailabilityManager';

export default function TechnicianProfile() {
  const { user, refreshMe } = useAuth();
  const notify = useNotifications();
  const [tech, setTech] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    technicianService.getProfile().then((t) => {
      setTech(t);
      setForm({
        firstName: t.firstName || '', lastName: t.lastName || '', phoneNumber: t.phoneNumber || '',
        bio: t.bio || '', yearsOfExperience: t.yearsOfExperience ?? '', certificationNumber: t.certificationNumber || '',
        address: t.address || '', city: t.city || '', state: t.state || '', zipCode: t.zipCode || '',
        hourlyRate: t.hourlyRate ?? '', servicesOffered: (t.servicesOffered || []).join(', '),
      });
    }).catch((e) => notify.apiError(e));
  }, [notify]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        yearsOfExperience: form.yearsOfExperience === '' ? null : Number(form.yearsOfExperience),
        hourlyRate: form.hourlyRate === '' ? null : Number(form.hourlyRate),
        servicesOffered: form.servicesOffered
          ? form.servicesOffered.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
      };
      const updated = await technicianService.updateProfile(payload);
      setTech(updated);
      await refreshMe();
      notify.success('Profile saved');
    } catch (err) {
      notify.apiError(err);
    } finally {
      setBusy(false);
    }
  };

  const uploadCert = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const updated = await technicianService.uploadCertification(file);
      setTech(updated);
      notify.success('Certification uploaded');
    } catch (err) {
      notify.apiError(err);
    }
  };

  if (!form || !tech) return <div className="page"><div className="container"><LoadingSpinner /></div></div>;

  return (
    <div className="page">
      <div className="container stack" style={{ gap: '1.25rem', maxWidth: 780 }}>
        <h1>My profile</h1>

        <AvailabilityManager
          available={tech.availableForWork}
          disabled={user?.status !== 'ACTIVE'}
          onChange={(v) => setTech((t) => ({ ...t, availableForWork: v }))}
        />

        <form className="card pad" onSubmit={save}>
          <div className="form-grid">
            <Field label="First name" value={form.firstName} onChange={set('firstName')} />
            <Field label="Last name" value={form.lastName} onChange={set('lastName')} />
            <Field label="Phone" value={form.phoneNumber} onChange={set('phoneNumber')} />
            <Field label="Hourly rate (USD)" type="number" min="0" step="0.01" value={form.hourlyRate} onChange={set('hourlyRate')} />
            <Field label="Years of experience" type="number" min="0" value={form.yearsOfExperience} onChange={set('yearsOfExperience')} />
            <Field label="Certification number" value={form.certificationNumber} onChange={set('certificationNumber')} />
            <Field label="Address" value={form.address} onChange={set('address')} />
            <Field label="City" value={form.city} onChange={set('city')} />
            <Field label="State / region" value={form.state} onChange={set('state')} />
            <Field label="Postal code" value={form.zipCode} onChange={set('zipCode')} />
          </div>
          <Field label="Services offered (comma separated)" value={form.servicesOffered} onChange={set('servicesOffered')} />
          <Field as="textarea" label="Bio" value={form.bio} onChange={set('bio')} maxLength={2000} />
          <button className="btn block" disabled={busy}>{busy ? 'Saving…' : 'Save profile'}</button>
        </form>

        <div className="card pad stack">
          <strong>Certifications</strong>
          {tech.certificationUrls?.length ? (
            <ul className="stack" style={{ gap: '.35rem' }}>
              {tech.certificationUrls.map((u) => (
                <li key={u}><a href={u} target="_blank" rel="noreferrer"><i className="bi bi-file-earmark-text" /> {u.split('/').pop()}</a></li>
              ))}
            </ul>
          ) : (
            <p className="muted" style={{ fontSize: '.9rem' }}>None uploaded.</p>
          )}
          <label className="btn ghost sm" style={{ alignSelf: 'flex-start' }}>
            <i className="bi bi-upload" /> Upload
            <input type="file" hidden onChange={uploadCert} accept="image/*,.pdf" />
          </label>
        </div>
      </div>
    </div>
  );
}
